<template>
    <!-- Background element -->
    <div
        v-if="bgElement?.backgroundElement_Image"
        aria-hidden="true"
        class="absolute flex w-full h-full inset-0 pointer-events-none"
        :class="[bgOpacity, {
            'items-center': ['left', 'center', 'right'].includes(bgElement.backgroundElement_Placement),
            'items-end': ['bottom-left', 'bottom', 'bottom-right'].includes(bgElement.backgroundElement_Placement),
            'items-start': ['top-left', 'top', 'top-right'].includes(bgElement.backgroundElement_Placement),
            'justify-start': ['top-left', 'left', 'bottom-left'].includes(bgElement.backgroundElement_Placement),
            'justify-end': ['top-right', 'right', 'bottom-right'].includes(bgElement.backgroundElement_Placement),
            'justify-center': ['top', 'center', 'bottom'].includes(bgElement.backgroundElement_Placement),
        }]"
    >
        <div
            v-if="isSvg"
            v-element-visibility="onElementVisibility"
            class="relative"
            :class="[bgSize, {
                'w-full': bgElement.backgroundElement_ScaleWidth === true,
                'h-full': bgElement.backgroundElement_ScaleHeight === true,
                'object-cover': bgElement.backgroundElement_ScaleWidth === true && bgElement.backgroundElement_ScaleHeight === true,
            }]"
            :style="{
                transform: `translate(${horizontalOffset}, ${verticalOffset})`
            }"
            v-html="isVisible ? svgContent : ''"
        />

        <img
            v-else
            :src="backgroundImageUrl"
            :class="[bgSize, {
                'w-full': bgElement.backgroundElement_ScaleWidth === true,
                'h-full': bgElement.backgroundElement_ScaleHeight === true,
                'object-cover': bgElement.backgroundElement_ScaleWidth === true && bgElement.backgroundElement_ScaleHeight === true,
            }]"
            class="relative"
            :style="{
                transform: `translate(${horizontalOffset}, ${verticalOffset})`,
                objectPosition: `${bgElement?.backgroundElement_Image?.[0]?.focalPoint[0] * 100 ?? '50'}% ${bgElement?.backgroundElement_Image?.[0]?.focalPoint[1] * 100 ?? '50'}%`,
            }"
        >
    </div>
</template>

<script setup>
    import { vElementVisibility } from '@vueuse/components'
    import { backgroundElementQuery } from '~/queries/sections/backgroundElement'

    const { query } = useGraphQL()

    const props = defineProps({
        entry: Object,
    })

    const data = ref(null)
    const svgContent = ref('')
    const isVisible = ref(false)

    // Computed property to get the background element
    const bgElement = computed(() => {
        return data.value?.ds_BackgroundElementsEntries?.[0]
    })

    // Computed property to dynamically determine the image URL
    const backgroundImageUrl = computed(() => {
        const image = bgElement.value?.backgroundElement_Image?.[0]
        if (!image) return null
        return image.width < 1920 ? image.urlOriginal : image.url
    })

    // Check if the mimeType contains 'svg'
    const isSvg = computed(() => {
        return bgElement.value?.backgroundElement_Image?.[0]?.mimeType?.includes('svg')
    })

    const onElementVisibility = (state) => {
        if (!state) {
            return
        }

        // once the element is visible then its always visible
        isVisible.value = state
    }

    onMounted(() => {
        nextTick(() => {
            query({
                query: backgroundElementQuery,
                variables: {
                    canonicalUid: props.entry?.canonicalUid,
                },
                key: `background-element-${props.entry?.canonicalUid}`,
                server: false,
            })
                .then((response) => {
                    data.value = toValue(response)

                    // If the file is an SVG, fetch its content
                    if (isSvg.value) {
                        fetch(bgElement.value.backgroundElement_Image[0].url)
                            .then((res) => res.text())
                            .then((svg) => {
                                // Set width and height to 100% for the SVG
                                const parser = new DOMParser()
                                const svgDoc = parser.parseFromString(svg, 'image/svg+xml')
                                const svgElement = svgDoc.querySelector('svg')
                                if (svgElement) {
                                    svgElement.setAttribute('width', '100%')
                                    svgElement.setAttribute('height', '100%')
                                    svgContent.value = svgElement.outerHTML
                                }
                            })
                    }
                })
        })
    })

    // Other computed properties
    const bgSize = computed(() => {
        if (bgElement.value?.backgroundElement_ScaleWidth !== true && bgElement.value?.backgroundElement_ScaleHeight !== true) {
            switch (bgElement.value?.backgroundElement_Size) {
                case 'xs':
                    return 'h-[100px]'
                case 'sm':
                    return 'h-[200px]'
                case 'md':
                default:
                    return 'h-[300px]'
                case 'lg':
                    return 'h-[500px]'
                case 'xl':
                    return 'h-[800px]'
            }
        }
        return ''
    })

    const bgOpacity = computed(() => {
        switch (bgElement.value?.backgroundElement_Opacity) {
            case '5':
                return 'opacity-5'
            case '10':
                return 'opacity-10'
            case '20':
            default:
                return 'opacity-20'
            case '50':
                return 'opacity-50'
            case '100':
                return 'opacity-100'
        }
    })

    const getOffset = (value) => {
        if (!value) return '0'
        return value
    }

    const horizontalOffset = computed(() =>
        getOffset(bgElement.value?.backgroundElement_HorizontalAdjustment)
    )

    const verticalOffset = computed(() =>
        getOffset(bgElement.value?.backgroundElement_VerticalAdjustment)
    )
</script>
